<template>
  <div class="container mt-4">
    <!-- Page Header -->
    <div class="row mt-3 mb-4">
      <div class="col-12">
        <div class="card shadow-sm">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h4 class="mb-0 font-weight-bolder">Web3 Administration</h4>
                <p class="text-sm mb-0 text-muted">
                  Manage and monitor Web3 withdrawal processes.
                </p>
              </div>
              <router-link
                to="/admin"
                class="btn btn-outline-primary btn-sm mb-0"
              >
                <i class="fa fa-arrow-left me-1"></i> Back to Admin
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Withdrawal Summary Section -->
    <div class="row mt-3">
      <div class="col-12">
        <div class="card shadow-sm">
          <div class="card-header pb-0">
            <h5 class="mb-0 font-weight-bolder">
              <i class="fas fa-chart-pie me-2"></i>Withdrawal Summary
            </h5>
          </div>
          <div class="card-body pt-3">
            <div v-if="loadingSummary" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading summary...</span>
              </div>
              <p class="mt-2">Loading summary...</p>
            </div>
            <div v-if="summaryError" class="alert alert-danger text-white">
              <i class="fas fa-exclamation-triangle me-2"></i>{{ summaryError }}
            </div>
            <div v-if="!loadingSummary && !summaryError" class="row">
              <!-- Pending Withdrawals Column -->

              <div class="col-md-3 mb-3 mb-md-0">
                <DefaultCounterCard
                  :count="pendingWithdrawalsCount"
                  :alwaysCount="false"
                  title="Pending Transactions"
                  description="Awaiting processing"
                  cardColor="light"
                  color="dark"
                  shadow="hover"
                  :decimals="0"
                  iconClass="fas fa-hourglass-half text-warning"
                />
              </div>
              <div class="col-md-3">
                <DefaultCounterCard
                  :count="pendingWithdrawalsTotalXAP"
                  :alwaysCount="false"
                  :decimals="2"
                  title="Total XAP Pending"
                  description="Sum of XAP"
                  suffix=" XAP"
                  cardColor="light"
                  color="dark"
                  shadow="hover"
                  iconClass="fas fa-coins text-warning"
                />
              </div>

              <div class="col-md-3 mb-3 mb-md-0">
                <DefaultCounterCard
                  :count="completedWithdrawalsCount"
                  :alwaysCount="false"
                  title="Completed Transactions"
                  description="Successfully processed"
                  cardColor="light"
                  color="dark"
                  shadow="hover"
                  :decimals="2"
                  iconClass="fas fa-check-circle text-success"
                />
              </div>
              <div class="col-md-3">
                <DefaultCounterCard
                  :count="completedWithdrawalsTotalXAP"
                  :alwaysCount="false"
                  :decimals="2"
                  title="Total XAP Completed"
                  description="Sum of XAP"
                  suffix=" XAP"
                  cardColor="light"
                  color="dark"
                  shadow="hover"
                  iconClass="fas fa-coins text-success"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Execute Withdrawals Section -->
    <div class="row mt-4">
      <div class="col-12">
        <div class="card shadow-sm">
          <div class="card-body text-center">
            <h5 class="font-weight-bolder">
              <i class="fas fa-tasks me-2"></i>Process Withdrawals
            </h5>
            <p class="text-sm text-muted">Execute all pending withdrawals.</p>
            <button
              class="btn btn-primary btn-md mt-3 px-5"
              @click="promptForPassword"
              :disabled="
                !hasPendingWithdrawals || executingWithdrawal || loadingSummary
              "
            >
              <span
                v-if="executingWithdrawal"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              <i v-else class="fas fa-cogs me-2"></i>
              Process Pending Withdrawals
            </button>
            <div
              v-if="executionMessage"
              :class="executionSuccess ? 'text-success' : 'text-danger'"
              class="mt-3 font-weight-bold"
            >
              <i
                :class="
                  executionSuccess
                    ? 'fas fa-check-circle'
                    : 'fas fa-times-circle'
                "
                class="me-1"
              ></i>
              {{ executionMessage }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Password Modal -->
    <div
      class="modal fade"
      id="passwordModal"
      ref="passwordModal"
      tabindex="-1"
      aria-labelledby="passwordModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 id="passwordModalLabel" class="modal-title font-weight-bolder">
              Enter Password
            </h5>
            <button
              type="button"
              class="btn-close"
              aria-label="Close"
              @click="cancelPasswordPrompt"
            ></button>
          </div>
          <div class="modal-body">
            <p class="text-muted text-sm">
              Please enter your password to authorize withdrawal processing.
            </p>
            <div class="form-group">
              <div class="row">
                <div class="col-md-4">
                  <label for="password-s" class="form-label">Part S</label>
                  <input
                    ref="partS"
                    v-model="passwordPartS"
                    type="password"
                    id="password-s"
                    class="form-control form-control-lg text-center"
                    maxlength="255"
                  />
                </div>
                <div class="col-md-4">
                  <label for="password-a" class="form-label">Part A</label>
                  <input
                    ref="partA"
                    v-model="passwordPartA"
                    type="password"
                    id="password-a"
                    class="form-control form-control-lg text-center"
                    maxlength="255"
                  />
                </div>
                <div class="col-md-4">
                  <label for="password-n" class="form-label">Part N</label>
                  <input
                    ref="partN"
                    v-model="passwordPartN"
                    type="password"
                    id="password-n"
                    class="form-control form-control-lg text-center"
                    @keyup.enter="submitPassword"
                    maxlength="255"
                  />
                </div>
              </div>
            </div>
            <div v-if="passwordError" class="text-danger text-sm mt-2 mb-2">
              {{ passwordError }}
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-outline-secondary"
              @click="cancelPasswordPrompt"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!passwordPartS || !passwordPartA || !passwordPartN"
              @click="submitPassword"
            >
              Authorize & Proceed
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <Toast />
</template>

<script>
import axios from "axios";
import Toast from "primevue/toast";
import DefaultCounterCard from "@/examples/Cards/DefaultCounterCard.vue"; // Corrected path
import { Modal } from "bootstrap";

export default {
  name: "AdminWeb3",
  components: {
    Toast,
    DefaultCounterCard,
  },
  data() {
    return {
      // web3: null, // Not actively used based on current requirements
      withdrawalsSummary: [],
      loadingSummary: false,
      summaryError: null,

      executingWithdrawal: false,
      executionMessage: null,
      executionSuccess: false,

      showPasswordModal: false,
      passwordInput: "",
      passwordPartS: "",
      passwordPartA: "",
      passwordPartN: "",
      passwordError: null,
      passwordModalInstance: null,
    };
  },
  watch: {
    showPasswordModal(value) {
      if (!this.passwordModalInstance) return;
      if (value) {
        this.passwordModalInstance.show();
      } else {
        this.passwordModalInstance.hide();
      }
    },
  },
  computed: {
    pendingWithdrawals() {
      return (
        this.withdrawalsSummary.find((item) => item._id === "pending") || {
          count: 0,
          totalXAP: 0,
        }
      );
    },
    completedWithdrawals() {
      return (
        this.withdrawalsSummary.find((item) => item._id === "completed") || {
          count: 0,
          totalXAP: 0,
        }
      );
    },
    pendingWithdrawalsCount() {
      return this.pendingWithdrawals.count;
    },
    pendingWithdrawalsTotalXAP() {
      return parseFloat(this.pendingWithdrawals.totalXAP) || 0;
    },
    completedWithdrawalsCount() {
      return this.completedWithdrawals.count;
    },
    completedWithdrawalsTotalXAP() {
      return parseFloat(this.completedWithdrawals.totalXAP) || 0;
    },
    hasPendingWithdrawals() {
      return this.pendingWithdrawalsCount > 0;
    },
  },
  mounted() {
    this.getWithdrawalsSummary();
    if (this.$refs.passwordModal) {
      this.passwordModalInstance = new Modal(this.$refs.passwordModal);

      this.$refs.passwordModal.addEventListener("hidden.bs.modal", () => {
        if (this.showPasswordModal) {
          this.cancelPasswordPrompt(true);
        }
      });

      this.$refs.passwordModal.addEventListener("shown.bs.modal", () => {
        this.$refs.partS.focus();
      });
    }
  },
  methods: {
    async getWithdrawalsSummary() {
      this.loadingSummary = true;
      this.summaryError = null;
      try {
        // Simulate API delay for loading state
        // await new Promise(resolve => setTimeout(resolve, 1500));
        const response = await axios.get(
          this.$store.getters["getServerURL"] + "/admin/getWithdrawalsSummary",
          {
            headers: { Authorization: localStorage.getItem("jwt") },
          }
        );
        this.withdrawalsSummary = response.data;
        if (!Array.isArray(response.data) || response.data.length === 0) {
          // Ensure there's always some data structure for computed properties
          this.withdrawalsSummary = [
            { _id: "pending", count: 0, totalXAP: 0 },
            { _id: "completed", count: 0, totalXAP: 0 },
          ];
          // Potentially show a specific message if API returns empty or unexpected data
          this.$toast.add({
            severity: "info",
            summary: "No Data",
            detail: "No withdrawal data found.",
            life: 3000,
          });
        }
      } catch (error) {
        console.error("Error fetching withdrawals summary:", error);
        this.summaryError =
          "Failed to load withdrawals summary. " +
          (error.response?.data?.message || error.message);
        this.$toast.add({
          severity: "error",
          summary: "Summary Error",
          detail: this.summaryError,
          life: 5000, // Longer for errors
        });
      } finally {
        this.loadingSummary = false;
      }
    },
    promptForPassword() {
      if (!this.hasPendingWithdrawals) {
        this.$toast.add({
          severity: "warn",
          summary: "No Pending Withdrawals",
          detail: "There are no pending withdrawals to process.",
          life: 3000,
        });
        return;
      }
      this.passwordInput = ""; // Clear previous input
      this.passwordPartS = "";
      this.passwordPartA = "";
      this.passwordPartN = "";
      this.passwordError = null;
      this.executionMessage = null; // Clear previous messages
      this.showPasswordModal = true;
    },
    cancelPasswordPrompt(isModalEvent = false) {
      if (!this.showPasswordModal) return;

      this.showPasswordModal = false;
      this.passwordInput = "";
      this.passwordPartS = "";
      this.passwordPartA = "";
      this.passwordPartN = "";
      this.passwordError = null;

      if (!isModalEvent) {
        this.$toast.add({
          severity: "info",
          summary: "Cancelled",
          detail: "Withdrawal process cancelled.",
          life: 3000,
        });
      }
    },
    async submitPassword() {
      // Combine the parts
      this.passwordInput =
        this.passwordPartS + this.passwordPartA + this.passwordPartN;

      if (!this.passwordInput) {
        this.passwordError = "Password cannot be empty.";
        return;
      }

      const serverURL = this.$store.getters["getServerURL"];
      const isServerSecure = serverURL.startsWith("https://");
      const isClientSecure = window.location.protocol === "https:";

      if (
        process.env.NODE_ENV === "production" &&
        (!isServerSecure || !isClientSecure)
      ) {
        this.passwordError =
          "For security, password submission is only allowed over HTTPS.";
        this.$toast.add({
          severity: "error",
          summary: "Insecure Connection",
          detail: this.passwordError,
          life: 5000,
        });
        return;
      }

      this.passwordError = null;
      try {
        await this.executeWithdrawals(this.passwordInput);
        this.showPasswordModal = false;
      } catch (error) {
        this.passwordError = error.message;
      }
    },
    focusNext(event, nextFieldRef) {
      if (event.target.value) {
        this.$refs[nextFieldRef].focus();
      }
    },
    async executeWithdrawals(password) {
      this.executingWithdrawal = true;
      this.executionMessage = null;
      this.executionSuccess = false;

      try {
        // Simulate API delay
        // await new Promise(resolve => setTimeout(resolve, 2000));
        const response = await axios.post(
          this.$store.getters["getServerURL"] + "/admin/executeWithdrawal",
          { password: password },
          {
            headers: { Authorization: localStorage.getItem("jwt") },
          }
        );
        this.executionMessage =
          "Withdrawals processed successfully! " +
          JSON.stringify(response.data.message);
        this.executionSuccess = true;
        this.$toast.add({
          severity: "success",
          summary: "Execution Success",
          detail: this.executionMessage,
          life: 3000,
        });

        this.getWithdrawalsSummary(); // Refresh summary
      } catch (error) {
        console.error("Error executing withdrawals:", error);
        this.executionMessage =
          "Failed to execute withdrawals. " +
          (error.response?.data?.message ||
            JSON.stringify(error.response?.data) ||
            error.message);
        if (error.response && error.response.status === 401) {
          // Specifically handle auth errors like incorrect password
          this.executionMessage =
            "Authorization failed. Please check your password.";
        }
        this.executionSuccess = false;
        this.$toast.add({
          severity: "error",
          summary: "Execution Error",
          detail: this.executionMessage,
          life: 5000, // Longer for errors
        });
      } finally {
        this.executingWithdrawal = false;
      }
    },
  },
};
</script>

<style scoped>
/* Add some hover effects for DefaultCounterCard if not already present by its 'shadow' prop */
.card.shadow-hover:hover {
  transform: translateY(-3px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

/* Responsive adjustments for counter cards if needed */
@media (max-width: 767.98px) {
  .col-lg-6 .col-md-6:not(:last-child) {
    margin-bottom: 1rem; /* Add some space between cards on smaller screens if they stack */
  }
}

.btn-lg {
  padding: 0.75rem 1.5rem;
  font-size: 1.1rem;
}
</style>
